// Local-mode fixtures for Settings: Recording Storage and Logs Configuration.
// Development only — wired in by ../index.js / mockData.js when
// REACT_APP_LOCAL_MODE is "true".
//
// Each entry is { pattern, respond(body) }: `pattern` is a lowercase substring
// of the request URL, `respond` gets the parsed JSON body and returns the
// response data. The local-mode adapter always answers 200, so the 400 / 422
// refusals of /recordingStorage/set are not reachable here; a "fail" bucket
// name makes the test fail in /recordingStorage/test instead.

const iso = (msAgo) => new Date(Date.now() - msAgo).toISOString();

// Mutable so a save is visible on the next get, as with the real API.
const storage = {
  config: {
    orgId: 1,
    provider: "s3",
    bucket: "acme-ssh-recordings",
    region: "eu-west-1",
    prefix: "authnull/recordings",
    sse: "aws:kms",
    kmsKeyId: "arn:aws:kms:eu-west-1:111122223333:key/1234abcd-12ab-34cd-56ef-1234567890ab",
    credentialMode: "static",
    hasCredential: true,
    revision: 3,
    updatedAt: iso(3 * 86400e3),
    lastTestAt: iso(2 * 3600e3),
    lastTestOk: true,
  },
  recordings: { local: 5, pending: 2, failing: 1, held: 1, remote: 12 },
};

const omitEmpty = (o) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v !== false && v != null));

function setStorage(body = {}) {
  const provider = (body.provider || "local").toLowerCase();
  const prev = storage.config;
  const remote = provider === "s3" || provider === "gcs";
  const creds = body.credentials;
  const typed = !!creds && Object.values(creds).some(Boolean);
  const hasCredential = remote && body.credentialMode === "static" && (typed || (prev.hasCredential && prev.provider === provider));
  const test = remote ? { ok: true, deleteAllowed: provider !== "gcs", ...(provider === "gcs" ? { warning: "the test object .authnull-probe/0f3a could not be deleted (403 Forbidden); recordings do not need delete, so this is only a note" } : {}) } : null;
  storage.config = omitEmpty({
    orgId: 1,
    provider,
    ...(remote
      ? {
          bucket: body.bucket,
          region: body.region,
          endpoint: body.endpoint,
          prefix: body.prefix,
          pathStyle: body.pathStyle,
          sse: body.sse,
          kmsKeyId: body.kmsKeyId,
          credentialMode: body.credentialMode || "ambient",
        }
      : { credentialMode: "ambient" }),
    hasCredential: !!hasCredential,
    revision: (prev.revision || 0) + 1,
    updatedAt: new Date().toISOString(),
    ...(remote ? { lastTestAt: new Date().toISOString(), lastTestOk: true, lastTestError: test?.warning } : {}),
  });
  storage.config.hasCredential = !!hasCredential;
  let backfilled = 0;
  if (remote && body.backfill) {
    backfilled = Math.max(0, storage.recordings.local - storage.recordings.pending - storage.recordings.held);
    storage.recordings = { ...storage.recordings, pending: storage.recordings.pending + backfilled };
  }
  return { config: storage.config, test, backfilled };
}

function testStorage() {
  const c = storage.config;
  if (c.provider !== "s3" && c.provider !== "gcs") {
    return { test: { ok: true, deleteAllowed: true }, message: "recordings are kept on the local volume" };
  }
  const ok = !/fail/.test(c.bucket || "");
  const test = ok
    ? { ok: true, deleteAllowed: true }
    : { ok: false, deleteAllowed: false, error: "write: operation error S3: PutObject, https response error StatusCode: 403, AccessDenied" };
  storage.config = { ...c, lastTestAt: new Date().toISOString(), lastTestOk: ok, ...(ok ? {} : { lastTestError: test.error }) };
  if (ok) delete storage.config.lastTestError;
  return { test };
}

function retryHeld() {
  const released = storage.recordings.held;
  storage.recordings = { ...storage.recordings, held: 0, pending: storage.recordings.pending + released };
  return { released };
}

// Logs Configuration: Syslog active, with the SSH gateway type selected. The
// Splunk secrets come back masked even though Syslog is active — the vault
// holds one set per tenant and the get API masks it whatever the output.
const logOutput = {
  code: 200,
  status: "success",
  message: "Log output setup fetched successfully",
  outputName: "Syslog",
  splunk: { Id: 0, TenantID: 0, OutputName: "", Host: "", Password: "", AuthToken: "********", HttpUsername: "", HttpPassword: "", Port: 0, Logtype: "" },
  elasticsearch: { Id: 0, TenantID: 0, OutputName: "", Host: "", Port: 0, LogType: "", IndexName: "", HttpUser: "", HttpPasswd: "", Tls: false, TlsVerify: false },
  http: { Id: 0, TenantID: 0, Host: "", Port: 0, Uri: "", HttpMethod: "", ContentType: "", CustomHeaders: "********", UseTls: false, TlsVerify: false, JsonDateKey: "", JsonDateFormat: "", MatchPattern: "", RetryLimit: 0, RetryDelaySec: 0, LogType: "" },
  syslog: { Id: 4, TenantID: 1, Host: "siem.acme.internal", Port: 6514, Mode: "TLS", SyslogFormat: "rfc5424", MessageKey: "message", Workers: 1, TlsVerify: true, MessagePattern: "", RetryLimit: 5, RetryDelaySec: 5, LogType: "Authentication Logs,SSH Gateway Logs" },
};

const saveLogOutput = (body = {}) => {
  // eslint-disable-next-line no-console
  console.info("[LOCAL MODE] log output save", { ...body, password: body.password ? "(set)" : "", authToken: body.authToken ? "(set)" : "", httpPassword: body.httpPassword ? "(set)" : "", customHeaders: body.customHeaders ? "(set)" : "" });
  if (body.customHeaders != null && typeof body.customHeaders !== "string") {
    return { error: "Invalid JSON format" };
  }
  logOutput.outputName = body.outputName || logOutput.outputName;
  return { code: 200, status: "success", message: "Log output setup created successfully" };
};

export const SETTINGS_MOCKS = [
  { pattern: "/recordingstorage/get", respond: () => ({ config: storage.config, recordings: storage.recordings }) },
  { pattern: "/recordingstorage/set", respond: (body) => setStorage(body || {}) },
  { pattern: "/recordingstorage/test", respond: () => testStorage() },
  { pattern: "/recordingstorage/retryheld", respond: () => retryHeld() },
  { pattern: "/getlogoutputconfiguration", respond: () => logOutput },
  { pattern: "/storelogoutputconfiguration", respond: (body) => saveLogOutput(body) },
  { pattern: "/updatelogoutputconfiguration", respond: (body) => saveLogOutput(body) },
];
